import { FunctionComponent, useMemo, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller } from "react-hook-form";
import * as yup from "yup";
import {
  Checkbox,
  CheckboxProps,
  FormControlLabel,
  FormHelperText,
  styled,
  Typography,
  useTheme,
} from "@mui/material";
import {
  PHONE_VALIDATION_RULE_MAPPING,
  useConfiguration,
} from "../../dataAccess/api/configuration";
import { useUser } from "../../dataAccess/api/user";
import { getJustificationByAlignment } from "../../builder/themeOptionsBuilder";
import TextField from "./TextField";
import { GuestFlowRedemption } from "../../dataAccess/api/deal";
import { ThemeButton } from "./ThemeComponents/ThemeButton";
import { checkIfEmailChanged, DealSettings } from "../public/Deal";
import { isGuestUser } from "../../dataAccess/api/guestUser";
import { ButtonProgress } from "./ButtonProgress";
import TermsText from "./TermsText";
import { useTranslation } from "../../helpers/i18n";
import {
  COMPANY_NAME_PLACEHOLDER_TEXT,
  EMAIL_ADDRESS_PLACEHOLDER_TEXT,
  FULL_NAME_PLACEHOLDER_TEXT,
  PHONE_NUMBER_PLACEHOLDER_TEXT,
} from "../../../theme/defaultConstants";
import HTMLRichText from "./HTMLRichText";
import { useForm } from "../../helpers/useForm";
import FilledCheckboxIcon from "../../assets/svg/filled-checkbox.svg";
import { replaceBracesKey } from "../../helpers/formatter";

interface Props
  extends Pick<
    DealSettings,
    | "redemptionButtons"
    | "companyNamePlaceholderText"
    | "emailAddressPlaceholderText"
    | "fullNamePlaceholderText"
    | "phoneNumberPlaceholderText"
    | "form"
  > {
  onSubmit: (data: GuestFlowRedemption) => void;
  loading?: boolean;
  guestUserCreated: boolean;
  coloredVariant: boolean;
  restrictedSize: boolean;
}

const StyledForm = styled("form")<Pick<Props["form"], "alignment">>(
  ({ theme, alignment }) => ({
    display: "flex",
    gap: theme.spacing(2),
    flexDirection: "column",
    alignItems: getJustificationByAlignment(alignment),
  }),
);

const CheckboxFieldContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
}));

const StyledInputsContainer = styled("div")<Pick<Props, "restrictedSize">>(
  ({ theme, restrictedSize }) => ({
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    alignItems: "center",
    gap: "1rem",
    [theme.breakpoints.up(restrictedSize ? "lg" : "md")]: {
      flexWrap: "nowrap",
      justifyContent: "initial",
      alignItems: "flex-start",
    },
  }),
);

const Terms = styled(FormControlLabel)(() => ({
  margin: 0,
}));

const StyledCheckbox = styled(
  (props: CheckboxProps & { customColor?: string }) => (
    <Checkbox
      sx={{
        color: props.customColor,
        "&.Mui-checked": {
          color: props.customColor,
        },
      }}
      icon={
        props.customColor ? <FilledCheckboxIcon fontSize="medium" /> : undefined
      }
      {...props}
    />
  ),
)(({ theme }) => ({
  padding: 0,
  margin: theme.spacing(1),
  overflow: "visible",
  marginLeft: 0,
  "& .MuiTouchRipple-root": {
    width: 32,
    height: 32,
    top: "50%",
    left: "50%",
    bottom: "initial",
    right: "initial",
    transform: "translate(-50%, -50%)",
  },
}));

const StyledTextField = styled(TextField)<Pick<Props, "restrictedSize">>(
  ({ theme, restrictedSize }) => ({
    width: "100%",
    [theme.breakpoints.up(restrictedSize ? "lg" : "md")]: {
      "& .MuiFilledInput-root": {
        marginBottom: "0",
      },
      width: "auto",
    },
  }),
);

const StyledButton = styled(ThemeButton)<Pick<Props, "restrictedSize">>(
  ({ theme, restrictedSize }) => ({
    marginRight: "0",
    marginBottom: "0.5rem",
    minWidth: "8rem",
    width: "100%",
    marginTop: 0,
    [theme.breakpoints.up(restrictedSize ? "lg" : "md")]: {
      width: "auto",
      minWidth: 150,
      marginBottom: "0",
    },
  }),
);

const defaultPhoneValidationRule: keyof typeof PHONE_VALIDATION_RULE_MAPPING =
  "numbers_letters_spaces_dash";
const defaultPhoneMinLength = 3;
const defaultPhoneMaxLength = 32;

const termsAndConditionsErrorId = "bf-terms-and-conditions-error";
const acknowledgmentErrorId = "bf-acknowledgment-terms-error";

const DealForm: FunctionComponent<Props> = ({
  loading,
  guestUserCreated,
  onSubmit,
  redemptionButtons,
  companyNamePlaceholderText,
  emailAddressPlaceholderText,
  fullNamePlaceholderText,
  phoneNumberPlaceholderText,
  coloredVariant,
  restrictedSize,
  form,
}: Props) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const { configuration } = useConfiguration();
  const { settings } = useTheme();
  const showTermsAndConditions =
    !form?.acknowledgmentHtml &&
    (!configuration?.customDomain || settings?.termsAndConditions);

  const { communityToggleSetting } = configuration || {};
  const communityToggleSettingWithDefaults = useMemo(
    () => ({
      phoneValidationRule: defaultPhoneValidationRule,
      phoneValidationMinLength: defaultPhoneMinLength,
      phoneValidationMaxLength: defaultPhoneMaxLength,
      ...communityToggleSetting,
    }),
    [communityToggleSetting],
  );

  const validationSchema = useMemo(() => {
    let schema = yup.object().shape({
      email: yup
        .string()
        .transform((value) => value.trim())
        .email(t("information_email_valid"))
        .required(t("information_email_required")),
      signUpOrganizationName: yup
        .string()
        .required(t("information_organization_name_required")),
      fullName: yup.string().required(t("information_user_full_name_required")),
      ...(form?.collectPhoneNumber
        ? {
            phoneNumber: yup
              .string()
              .required(t("information_phone_number_required"))
              .min(
                communityToggleSettingWithDefaults.phoneValidationMinLength,
                replaceBracesKey(t("information_phone_number_min_length"), {
                  key: "min_length",
                  value:
                    communityToggleSettingWithDefaults.phoneValidationMinLength.toString(),
                }),
              )
              .max(
                communityToggleSettingWithDefaults.phoneValidationMaxLength,
                replaceBracesKey(t("information_phone_number_max_length"), {
                  key: "max_length",
                  value:
                    communityToggleSettingWithDefaults.phoneValidationMaxLength.toString(),
                }),
              )
              .matches(
                PHONE_VALIDATION_RULE_MAPPING[
                  communityToggleSettingWithDefaults.phoneValidationRule
                ],
                replaceBracesKey(t("information_phone_number_invalid_format"), {
                  key: "rule_name",
                  value: t(
                    `information_phone_number_${communityToggleSettingWithDefaults.phoneValidationRule}`,
                  ),
                }),
              ),
          }
        : {}),
    });

    if (form?.acknowledgmentHtml) {
      schema = schema.shape({
        acknowledgmentTerms: yup
          .boolean()
          .oneOf([true], t("information_acknowledgment_required")),
      });
    }

    if (showTermsAndConditions) {
      schema = schema.shape({
        termsAndConditions: yup
          .boolean()
          .oneOf([true], t("information_terms_and_conditons")),
      });
    }

    return schema;
  }, [communityToggleSettingWithDefaults, showTermsAndConditions, form, t]);

  const userEmail = user?.email;
  const userOrgName = user?.organization?.name || user?.signUpOrganizationName;
  const userFirstName = user?.firstName;
  const userLastName = user?.lastName;
  const userPhoneNumber = user?.phoneNumber || "";
  const guestUser = isGuestUser(user);
  const { control, formState, handleSubmit } = useForm<GuestFlowRedemption>({
    shouldFocusError: true,
    resolver: yupResolver(validationSchema),
    defaultValues: {
      email: userEmail,
      signUpOrganizationName: userOrgName,
      fullName:
        userFirstName || userLastName
          ? `${userFirstName || ""} ${userLastName || ""}`.trim()
          : "",
      termsAndConditions: false,
      acknowledgmentTerms: false,
      ...(form?.collectPhoneNumber
        ? {
            phoneNumber: userPhoneNumber,
          }
        : {}),
    },
  });

  const [hadEmailChanged, setHadEmailChanged] = useState(false);

  function updateHadEmailChanged(e: React.ChangeEvent<HTMLInputElement>) {
    return setHadEmailChanged(checkIfEmailChanged(e.target.value, userEmail));
  }

  const { claimNow, getApproval, resendApproval } =
    redemptionButtons?.emailRedemptions || {};
  const claimText = claimNow;
  const approvalText =
    guestUserCreated && !hadEmailChanged ? resendApproval : getApproval;
  const submitButton = (
    <StyledButton
      startIcon={loading ? <ButtonProgress /> : null}
      variant="contained"
      type="submit"
      disabled={loading}
      restrictedSize={restrictedSize}
    >
      {user?.id && !guestUser && !hadEmailChanged ? claimText : approvalText}
    </StyledButton>
  );

  return (
    <StyledForm
      alignment={form?.alignment}
      onSubmit={handleSubmit(onSubmit)}
      aria-label={t("accessibility_registration_form")}
    >
      {form?.textBoxAbove && <HTMLRichText html={form.textBoxAbove} />}
      <StyledInputsContainer restrictedSize={restrictedSize}>
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <StyledTextField
              restrictedSize={restrictedSize}
              {...field}
              inputRef={field.ref}
              labelPosition={form?.inputLabelPosition}
              label={
                emailAddressPlaceholderText || EMAIL_ADDRESS_PLACEHOLDER_TEXT
              }
              onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateHadEmailChanged(e)
              }
              error={!!formState.errors.email}
              helperText={
                formState.errors.email ? formState.errors.email.message : ""
              }
            />
          )}
        />
        <Controller
          name="signUpOrganizationName"
          control={control}
          render={({ field }) => (
            <StyledTextField
              restrictedSize={restrictedSize}
              {...field}
              inputRef={field.ref}
              labelPosition={form?.inputLabelPosition}
              label={
                companyNamePlaceholderText || COMPANY_NAME_PLACEHOLDER_TEXT
              }
              error={!!formState.errors.signUpOrganizationName}
              helperText={
                formState.errors.signUpOrganizationName
                  ? formState.errors.signUpOrganizationName.message
                  : ""
              }
            />
          )}
        />
        <Controller
          name="fullName"
          control={control}
          render={({ field }) => (
            <StyledTextField
              restrictedSize={restrictedSize}
              {...field}
              inputRef={field.ref}
              labelPosition={form?.inputLabelPosition}
              label={fullNamePlaceholderText || FULL_NAME_PLACEHOLDER_TEXT}
              error={!!formState.errors.fullName}
              helperText={
                formState.errors.fullName
                  ? formState.errors.fullName.message
                  : ""
              }
            />
          )}
        />
        {form?.collectPhoneNumber && (
          <Controller
            name="phoneNumber"
            control={control}
            render={({ field }) => (
              <StyledTextField
                restrictedSize={restrictedSize}
                {...field}
                inputRef={field.ref}
                labelPosition={form?.inputLabelPosition}
                label={
                  phoneNumberPlaceholderText || PHONE_NUMBER_PLACEHOLDER_TEXT
                }
                error={!!formState.errors.phoneNumber}
                helperText={
                  formState.errors.phoneNumber
                    ? formState.errors.phoneNumber.message
                    : ""
                }
              />
            )}
          />
        )}
      </StyledInputsContainer>
      {form?.textBoxBelow && <HTMLRichText html={form.textBoxBelow} />}
      {showTermsAndConditions && (
        <Controller
          name="termsAndConditions"
          control={control}
          render={({ field }) => (
            <CheckboxFieldContainer>
              <Terms
                control={
                  <StyledCheckbox
                    {...field}
                    inputRef={field.ref}
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    customColor={
                      coloredVariant ? form?.formTextColor : undefined
                    }
                    inputProps={{
                      "aria-describedby": formState.errors.termsAndConditions
                        ? termsAndConditionsErrorId
                        : undefined,
                    }}
                  />
                }
                label={
                  <Typography>
                    <TermsText />
                  </Typography>
                }
              />
              {formState.errors.termsAndConditions && (
                <FormHelperText error id={termsAndConditionsErrorId}>
                  {formState.errors.termsAndConditions.message}
                </FormHelperText>
              )}
            </CheckboxFieldContainer>
          )}
        />
      )}
      {form?.acknowledgmentHtml && (
        <Controller
          name="acknowledgmentTerms"
          control={control}
          render={({ field }) => (
            <CheckboxFieldContainer>
              <Terms
                control={
                  <StyledCheckbox
                    {...field}
                    inputRef={field.ref}
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    customColor={
                      coloredVariant ? form?.formTextColor : undefined
                    }
                    inputProps={{
                      "aria-describedby": formState.errors.acknowledgmentTerms
                        ? acknowledgmentErrorId
                        : undefined,
                    }}
                  />
                }
                label={<HTMLRichText html={form.acknowledgmentHtml} />}
              />
              {formState.errors.acknowledgmentTerms && (
                <FormHelperText error id={acknowledgmentErrorId}>
                  {formState.errors.acknowledgmentTerms.message}
                </FormHelperText>
              )}
            </CheckboxFieldContainer>
          )}
        />
      )}
      {submitButton}
    </StyledForm>
  );
};

export default DealForm;
